/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarCheck, ClipboardList, FileText, Plus, TrendingUp, Users } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { QueryState } from '../../components/ui/query-state.js';
import { StatCard } from '../../components/ui/stat-card.js';
import {
  type CaseloadClient,
  type TherapistDashboard,
  type TherapistScheduleItem,
  type TherapistTask,
  type WeeklyPoint,
} from '../../lib/api/therapist-dashboard.js';
import { useTherapistDashboard } from '../../lib/queries/therapist-dashboard.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';
import { DashboardCard } from '../admin/dashboard-card.js';

const STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'rose',
  no_show: 'slate',
};

function StatsRow({ stats }: { readonly stats: TherapistDashboard['stats'] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={Users}
        label={t('therapist.dashboardHome.totalClients')}
        value={String(stats.totalClients)}
        accent="bg-[var(--accent)]"
      />
      <StatCard
        icon={CalendarCheck}
        label={t('therapist.dashboardHome.sessionsToday')}
        value={String(stats.sessionsToday)}
        accent="bg-emerald-600"
        hint={t('therapist.dashboardHome.sessionsCompleted', { count: stats.sessionsTodayDone })}
      />
      <StatCard
        icon={ClipboardList}
        label={t('therapist.dashboardHome.pendingNotes')}
        value={String(stats.pendingNotes)}
        accent="bg-orange-500"
        hint={t('therapist.dashboardHome.dueToday')}
      />
      <StatCard
        icon={TrendingUp}
        label={t('therapist.dashboardHome.avgProgress')}
        value={stats.avgProgressPct !== null ? `${String(stats.avgProgressPct)}%` : '—'}
        accent="bg-[var(--accent)]"
        hint={t('therapist.dashboardHome.caseloadAvg')}
      />
    </div>
  );
}

function ScheduleCard({
  items,
}: {
  readonly items: readonly TherapistScheduleItem[];
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <DashboardCard
      title={t('therapist.dashboardHome.todaySchedule')}
      subtitle={t('therapist.dashboardHome.scheduledCount', { count: items.length })}
    >
      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          {t('therapist.dashboardHome.noSchedule')}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-3 rounded-xl border-l-4 border-[var(--accent)] bg-slate-50 p-3 dark:bg-slate-800/50"
            >
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {formatIst(s.startIso, 'h:mm a')}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                  {s.clientName}
                </p>
                <p className="truncate text-xs text-slate-400">{s.sessionType ?? '—'}</p>
              </div>
              <Badge label={t(`status.${s.status}`)} tone={STATUS_TONE[s.status] ?? 'slate'} />
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

function TasksCard({ tasks }: { readonly tasks: readonly TherapistTask[] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <DashboardCard
      title={t('therapist.dashboardHome.pendingTasks')}
      subtitle={t('therapist.dashboardHome.tasksRemaining', { count: tasks.length })}
    >
      {tasks.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          {t('therapist.dashboardHome.noTasks')}
        </p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-center gap-3 py-1.5">
              <FileText className="h-4 w-4 shrink-0 text-orange-500" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">
                {t('therapist.dashboardHome.completeNoteFor', { name: task.clientName })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

function ProgressTrendCard({ weekly }: { readonly weekly: readonly WeeklyPoint[] }): JSX.Element {
  const { t } = useTranslation();
  const data = weekly.map((w, i) => ({
    label: t('therapist.dashboardHome.weekLabel', { n: i + 1 }),
    sessions: w.sessions,
  }));
  return (
    <DashboardCard
      title={t('therapist.dashboardHome.progressTrend')}
      subtitle={t('therapist.dashboardHome.progressTrendSub')}
    >
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -16 }}>
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5b47db" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#5b47db" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} width={28} />
          <Tooltip />
          <Area
            type="monotone"
            dataKey="sessions"
            name={t('therapist.dashboardHome.sessionsToday')}
            stroke="#5b47db"
            strokeWidth={2}
            fill="url(#trendFill)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </DashboardCard>
  );
}

function CaseloadRow({ client }: { readonly client: CaseloadClient }): JSX.Element {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3 py-3 first:pt-0">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
        {initialsOf(client.name)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {client.name}
        </p>
        <p className="truncate text-xs text-slate-400">
          {client.nextSessionIso !== null
            ? t('therapist.dashboardHome.nextSession', {
                when: formatIst(client.nextSessionIso, 'EEE, h:mm a'),
              })
            : t('therapist.dashboardHome.noUpcoming')}
        </p>
      </div>
      <div className="w-24 shrink-0">
        <div className="mb-1 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
          {client.progressPct}%
        </div>
        <ProgressBar value={client.progressPct} />
      </div>
    </li>
  );
}

function CaseloadCard({ clients }: { readonly clients: readonly CaseloadClient[] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <DashboardCard
      title={t('therapist.dashboardHome.myClients')}
      subtitle={t('therapist.dashboardHome.activeCaseload')}
      action={
        <Link
          to="/therapist/clients"
          className="focus-ring text-sm font-medium text-[var(--accent-strong)]"
        >
          {t('therapist.dashboardHome.viewAll')}
        </Link>
      }
    >
      {clients.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          {t('therapist.dashboardHome.noClients')}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {clients.slice(0, 6).map((client) => (
            <CaseloadRow key={client.id} client={client} />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

/** Therapist → Dashboard (Figma). Live, self-scoped caseload metrics. */
export function TherapistDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const dashboard = useTherapistDashboard();

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          {t('therapist.dashboardHome.title')}
        </h1>
        <Link
          to="/therapist/schedule?new=1"
          className="focus-ring bg-brand-600 hover:bg-brand-700 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('therapist.dashboardHome.newSession')}
        </Link>
      </div>

      <QueryState
        isLoading={dashboard.isLoading}
        isError={dashboard.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {dashboard.data !== undefined && (
          <div className="space-y-6">
            <StatsRow stats={dashboard.data.stats} />
            <div className="grid gap-6 lg:grid-cols-2">
              <ScheduleCard items={dashboard.data.todaySchedule} />
              <TasksCard tasks={dashboard.data.pendingTasks} />
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <ProgressTrendCard weekly={dashboard.data.weeklyActivity} />
              <CaseloadCard clients={dashboard.data.clients} />
            </div>
          </div>
        )}
      </QueryState>
    </div>
  );
}
